import { useState, useEffect, useContext, useMemo } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useParams } from "react-router";
import { PRODUCTS_CONNECTION } from "../../graphql/queries";
import {
    UPDATE_PRODUCT,
    UPDATE_MANY_DYNOES,
    UPDATE_CATALOG,
} from "../../graphql/mutations";
import { OFFLINE } from "../../constants";
import { useStore } from "../../Store";
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';

let PageSize = 10;
export const useProducts = () => {
    const {user, publisher, userRole} = useContext(PublisherAndUserContext);
    const {catalg} = useStore((state) => state);
    const [archievedProducts, setArchievedProducts] = useState([]);
    const [status, setStatus] = useState({});
    const [catalog, setCatalog] = useState({
        id: "",
        catalogName: "",
        catalogStatus: "",
        products: [],
    });
    const [isCatalogAdmin, setIsCatalogAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalProducts, setTotalProducts] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [searchedProducts, setSearchedProducts] = useState([]);
    const [isProductListOrderChanged, setIsProductListOrderChanged] =
        useState(false);
    const { id: catalogId } = useParams();
    
    const [getProducts, { loading: productsLoading, fetchMore: productsFetchMore }] = useLazyQuery(PRODUCTS_CONNECTION, {
        onCompleted: (data) => {
            setTotalProducts(data.productsConnection.count);
            const isCatalogAdmin = !!catalg.catalogAdmins.find(
                (admin) => admin.id === user.id
            );
            setIsCatalogAdmin(isCatalogAdmin);
            
            const newArchievedProducts = data.productsConnection.edges.map(e => e.node).filter(
                (catalog) => catalog.productStatus === "Removed"
            );
            setArchievedProducts(newArchievedProducts);
            if (currentPage > 1) {
                const allProducts = [...catalog.products];
                const index = currentPage * 10 - 10;
                allProducts.splice(index, 10, ...data.productsConnection.edges.map(e => e.node));
                setCatalog({
                    ...catalg,
                    products: allProducts,
                });
              } else {
                const productsInit = new Array(totalProducts);
                productsInit.splice(0, 10, ...data.productsConnection.edges.map(e => e.node));
                setCatalog({
                    ...catalg,
                    products: productsInit,
                });
              }
            setLoading(false)
        },
        notifyOnNetworkStatusChange: true,
        onError: () => {
            setLoading(false)
        },
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });

    const onPageChange = (page) => {
        if (!catalog.products[(page - 1) * 10]) {
            productsFetchMore({
                variables: {
                    first: 10,
                    skip: Math.round(page * 10) - 10,
                },
          });
        }
        setCurrentPage(page);
    };

    const currentProductsList = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * PageSize;
        const lastPageIndex = firstPageIndex + PageSize;
        const resProducts = catalog.products.slice(firstPageIndex, lastPageIndex);
        if (!resProducts[0]) {
            return [];
        } else {
            return resProducts;
        }
    }, [currentPage, catalog]);

    const [updateProductMutation] = useMutation(UPDATE_PRODUCT, {
        onCompleted: (data) => {
            let productCopy = [...catalog.products];
            let index = productCopy.findIndex(
                (product) => product.id === data.updateProduct.id
            );
            productCopy[index] = {
                ...productCopy[index],
                productStatus: data.updateProduct.productStatus,
            };
            setCatalog({
                ...catalog,
                products: productCopy,
            });
            const newArchievedProducts = productCopy.filter(
                (product) => product.productStatus === "Removed"
            );
            setArchievedProducts(newArchievedProducts);
            let dynoes = [];
            for (let list of productCopy[index].productContent.dynoList) {
                dynoes.push(...list.dynosUnderList.map((listDyno) => listDyno.dyno.id));
            }
            if (data.updateProduct.productStatus !== "Removed") {
                updateManyDynoes({
                    variables: {
                        where: {
                            id: { in: dynoes },
                        },
                        publisherId: publisher?.id,
                        data: {
                            dynoPublishingStatus: {
                                set:
                                    data.updateProduct.productStatus === "Published"
                                        ? "AddedToProductLine"
                                        : "VerifiedByQA",
                            },
                        },
                    },
                });
            } else {
                setLoading(false)
            }
        },
        onError: () => {
            setLoading(false)
        },
    });

    const [updateManyDynoes] = useMutation(UPDATE_MANY_DYNOES, {
        onCompleted: (data) => {
            let productCopy = [...catalog.products];
            let index = productCopy.findIndex((product) => product.id === status.id);
            let dynoList = productCopy[index].productContent.dynoList;
            const dynoStatus =
                status.status === "Published" ? "AddedToProductLine" : "VerifiedByQA";
            dynoList = dynoList.map((list) => ({
                ...list,
                dynosUnderList: list.dynosUnderList.map((dyno) => ({
                    ...dyno,
                    dynoPublishingStatus: dynoStatus,
                })),
            }));
            productCopy[index] = {
                ...productCopy[index],
                productStatus: status.status,
                productContent: {
                    ...productCopy[index].productContent,
                    dynoList: dynoList,
                },
            };
            setCatalog({
                ...catalog,
                products: productCopy,
            });
            setLoading(false)
        },
        onError: () => {
            setLoading(false)
        },
    });

    const [updateCatalog] = useMutation(UPDATE_CATALOG);

    useEffect(() => {
        if (catalogId && user.id) {
            setLoading(true)
            getProducts({
                variables: {
                  first: 10,
                  where: {
                      catalogId: {
                          equals: catalogId
                      },
                  },
                },
              });
        }
    }, [catalogId, getProducts, user.id]);

    const updateProduct = (id) => {
        setLoading(true)
        updateProductMutation({
            variables: {
                data: {
                    productStatus: {
                        set: OFFLINE,
                    },
                },
                where: {
                    id: id,
                },
            },
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (isProductListOrderChanged) {
                setIsProductListOrderChanged(false);
                updateCatalog({
                    variables: {
                        where: {
                            id: catalog.id,
                        },
                        data: {
                            products: {
                                update: catalog.products.map((productList) => ({
                                    where: { id: productList.id },
                                    data: { index: {set: productList.index} },
                                })),
                            },
                        },
                    },
                });
            }
        }, 400);
        return () => {
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProductListOrderChanged]);

    const updateItemListOrder = (fromIndex, toIndex) => {
        let data = [...catalog.products];
        data[toIndex] = { ...data[toIndex], index: fromIndex + 1 };
        data[fromIndex] = { ...data[fromIndex], index: toIndex + 1 };
        setCatalog({
            ...catalog,
            products: data.sort((a, b) =>
                a.index < b.index ? -1 : a.index > b.index ? 1 : 0
            ),
        });
        setIsProductListOrderChanged(true);
    };

    return {
        user,
        catalog,
        updateItemListOrder,
        catalogId,
        archievedProducts,
        isCatalogAdmin,
        updateProduct,
        loading,
        publisher,
        userRole,
        totalProducts,
        PageSize,
        currentPage,
        onPageChange,
        searchText,
        searchedProducts,
        productsLoading,
        currentProductsList,
    }
}
